import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Instagram, Facebook, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const f = useLang().t.footer;
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(f.ok);
      setEmail("");
    } catch {
      toast.error(f.err);
    }
  };

  return (
    <footer data-testid="footer-section" className="bg-[#2B3A2F] text-[#EFE6DA] pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-[#EFE6DA]/15">
          <div className="lg:col-span-5">
            <Logo light className="h-20 md:h-24" />
            <p className="mt-6 text-[#D8CFC2] max-w-sm leading-relaxed">{f.tagline}</p>
          </div>
          <div className="lg:col-span-3 text-sm space-y-2 text-[#D8CFC2]">
            <p className="eyebrow text-[10px] text-[#D8C7B5] mb-4">{f.contacts}</p>
            <p data-testid="footer-phone"><a href="tel:+351966733195" className="hover:text-[#F7F5F0] transition-colors">+351 966733195</a> / <a href="tel:+351969484360" className="hover:text-[#F7F5F0] transition-colors">+351 969484360</a></p>
            <p data-testid="footer-email"><a href="mailto:casascomencanto.info@gmail.com" className="hover:text-[#F7F5F0] transition-colors break-all">casascomencanto.info@gmail.com</a></p>
            <div className="border-t border-[#EFE6DA]/15 mt-6 pt-6">
              <p className="eyebrow text-[10px] text-[#D8C7B5] mb-4">{f.addressLabel}</p>
              <div data-testid="footer-address" className="space-y-3">
                {[
                  ["Casa Das Maria's", "Beco da Horta 1, 7200-450 Reguengos de Monsaraz, Perolivas"],
                  ["Casa Recanto da Horta", "Beco da Horta n° 3, 7200-450 Reguengos de Monsaraz, Perolivas"],
                  ["Casa Coração Alentejano", "R. da Travessa 18, 7200-460 Reguengos de Monsaraz, Perolivas"],
                ].map(([n, a]) => (
                  <p key={n}><span className="text-[#F7F5F0]">{n}</span><br />{a}</p>
                ))}
              </div>
            </div>
          </div>
          <form onSubmit={subscribe} className="lg:col-span-4">
            <p className="eyebrow text-[10px] text-[#D8C7B5] mb-4">Newsletter</p>
            <div className="flex border-b border-[#EFE6DA]/40 focus-within:border-[#E8B49A] transition-colors">
              <input data-testid="footer-newsletter-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={f.emailPh} className="flex-1 bg-transparent outline-none py-3 placeholder:text-[#B5AC9F]" />
              <button data-testid="footer-newsletter-submit" type="submit" aria-label={f.subscribe} className="p-3 hover:translate-x-1 transition-transform"><ArrowRight className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-3 mt-8">
              {[[Instagram, "instagram", "https://www.instagram.com/casas_com_encanto/"], [Facebook, "facebook", "https://www.facebook.com/share/19fazKc6SM/"], [Mail, "mail", "mailto:casascomencanto.info@gmail.com"]].map(([I, k, href]) => (
                <a key={k} href={href} target={k === "mail" ? undefined : "_blank"} rel="noopener noreferrer" data-testid={`footer-social-${k}`} aria-label={k} className="w-11 h-11 rounded-full border border-[#EFE6DA]/30 flex items-center justify-center hover:bg-[#EFE6DA] hover:text-[#2B3A2F] transition-colors"><I className="w-4 h-4" /></a>
              ))}
            </div>
          </form>
        </div>
        <p className="font-display text-[12.5vw] lg:text-[11.5rem] leading-[0.85] tracking-[-0.04em] text-[#EFE6DA]/10 mt-12 select-none whitespace-nowrap">Casas com <em className="italic">EnCanto</em></p>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-[#B5AC9F] mt-8">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Casas com EnCanto. {f.rights}</p>
          <a
            data-testid="footer-complaints-book"
            href="https://www.livroreclamacoes.pt/Inicio/"
            target="_blank"
            rel="noopener noreferrer"
            className="self-start sm:self-auto inline-flex items-center gap-2 rounded-full border border-[#EFE6DA]/30 px-4 py-2 text-[#EFE6DA] hover:bg-[#EFE6DA] hover:text-[#2B3A2F] transition-colors"
          >
            Livro de Reclamações
          </a>
          <p>{f.made}</p>
        </div>
      </div>
    </footer>
  );
};
